"use client";

import React, {useMemo, useState} from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import {cn} from "@root/server/client/esweb/lib/utils.ts";
import type {UploadedFile} from "@root/server/client/esweb/src/lib/components/gallery/galleryTypes.ts";
import {buildPreviewUrl, normalizeUrl} from "@root/server/client/esweb/src/lib/components/gallery/galleryUtils.ts";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {GalleryImageWithLoader} from "./GalleryImageWithLoader.tsx";

import {Context} from "@hotwired/stimulus";



export const name = 'pws--gallery';

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <ReadOnlyGallery {...props}/>);
    }
}

interface ReadOnlyGalleryProps {
    files: UploadedFile[];
    className?: string;
    title?: string;
    emptyState?: React.ReactNode;
}


export function ReadOnlyGallery({files, className, title, emptyState}: ReadOnlyGalleryProps) {
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [open, setOpen] = useState(false);

    const slides = useMemo(
        () => files.map((file) => ({src: normalizeUrl(file.url), title: file.name})),
        [files],
    );

    if (!files?.length) {
        return (
            <div className={cn("rounded-lg border border-dashed border-muted-foreground/30 p-6 text-center text-sm text-muted-foreground", className)}>
                {emptyState ?? "Žádné fotografie nejsou k dispozici."}
            </div>
        );
    }

    return (
        <div className={cn("space-y-3", className)}>
            {title ? <h3 className="text-base font-semibold text-foreground">{title}</h3> : null}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {files.map((file, index) => {
                    return (
                        <button
                            key={file.url}
                            type="button"
                            className="group relative overflow-hidden rounded-lg border bg-muted"
                            onClick={() => {
                                setLightboxIndex(index);
                                setOpen(true);
                            }}
                            aria-label={`Zobrazit náhled ${file.name ?? "fotografie"}`}
                        >
                            <GalleryImageWithLoader
                                src={buildPreviewUrl(file.url)}
                                alt={file.name ?? "Fotografie"}
                                wrapperClassName="block h-full w-full"
                                className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                        </button>
                    );
                })}
            </div>
            <Lightbox
                open={open}
                close={() => setOpen(false)}
                slides={slides}
                index={lightboxIndex}
            />
        </div>
    );
}
